import { Dices } from 'lucide-react';
import { getConfidenceConfig } from '../../../utils/monteCarlo';

// Monte-Carlo statistics block — process-capability metrics (Cp, Cpk),
// distribution moments (μ, σ), and a 5-stop percentile strip. Renders
// only in MC mode below the comparison tiles + above the Histogram.
//
// The pre-reboot ResultsPanel had this block; it was dropped during
// the Phase 4 rewrite. User asked for it back ("Cpk, Fähigkeit etc.
// im Monte-Carlo-Ergebnis-Block").
//
// Capability badge thresholds follow the standard industry rule of
// thumb: ≥1.67 sehr fähig, ≥1.33 fähig, ≥1.00 grenzfähig, <1.00 nicht
// fähig. Surfaced as a small coloured pill next to the header so the
// verdict reads at a glance.

function capabilityBadge(cpk) {
  if (!Number.isFinite(cpk)) {
    return { label: '-', color: 'var(--color-ink-3)', bg: 'transparent' };
  }
  if (cpk >= 1.67) return { label: 'sehr fähig',  color: 'var(--color-ok)',   bg: 'color-mix(in srgb, var(--color-ok)  18%, transparent)' };
  if (cpk >= 1.33) return { label: 'fähig',        color: 'var(--color-ok)',   bg: 'color-mix(in srgb, var(--color-ok)  14%, transparent)' };
  if (cpk >= 1.00) return { label: 'grenzfähig',   color: 'var(--color-warn)', bg: 'color-mix(in srgb, var(--color-warn) 16%, transparent)' };
  return                   { label: 'nicht fähig', color: 'var(--color-err)',  bg: 'color-mix(in srgb, var(--color-err) 16%, transparent)' };
}

function Tile({ label, value, sub, accent, tooltip }) {
  return (
    <div
      className="bg-surface border border-line"
      title={tooltip}
      style={{
        borderRadius: 'var(--radius-md)', padding: '10px 12px',
        display: 'flex', flexDirection: 'column', gap: 2, minHeight: 64,
        minWidth: 0, // allow text-overflow to kick in inside the grid cell
        cursor: tooltip ? 'help' : undefined,
      }}
    >
      <div
        className="text-ink-3"
        style={{
          fontSize: 10, letterSpacing: 0.5, fontWeight: 700,
          // No uppercase: the labels are symbol-only ("μ", "σ") or
          // already conventional fixed-case identifiers ("Cp", "Cpk").
          // CSS-uppercase would mangle the Greek glyphs (μ→M, σ→Σ).
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}
      >
        {label}
      </div>
      <div
        className="num"
        style={{ fontSize: 18, fontWeight: 600, lineHeight: 1.1, color: accent || 'var(--color-ink)' }}
      >
        {value}
      </div>
      {sub && (
        <div
          className="text-ink-4"
          style={{
            fontSize: 10.5,
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}
        >
          {sub}
        </div>
      )}
    </div>
  );
}

function Pct({ label, value }) {
  return (
    <div
      className="bg-surface border border-line"
      style={{
        borderRadius: 'var(--radius-md)', padding: '6px 8px',
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1,
      }}
    >
      <div className="text-ink-4" style={{ fontSize: 9, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase' }}>
        {label}
      </div>
      <div className="num text-ink-2" style={{ fontSize: 11.5, fontWeight: 600 }}>
        {value}
      </div>
    </div>
  );
}

export default function MonteCarloStats({ mc }) {
  if (!mc) return null;

  const cp  = mc.capability?.cp;
  const cpk = mc.capability?.cpk;
  const badge = capabilityBadge(cpk);
  const conf = getConfidenceConfig(mc.confidence);

  const num = (v, d = 3) => (v === null || v === undefined || !Number.isFinite(v))
    ? '-'
    : Number(v).toFixed(d);

  const p = mc.percentiles || {};

  return (
    <div
      data-snippet="mc-stats"
      className="bg-surface border border-line"
      style={{ borderRadius: 'var(--radius-lg)', padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      {/* Header strip: sample count + confidence + Cpk verdict pill */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Dices size={14} className="text-brand" aria-hidden="true" />
          <div className="text-ink-2" style={{ fontSize: 12, fontWeight: 600 }}>
            Prozessfähigkeit
          </div>
          <span className="num text-ink-3" style={{ fontSize: 11 }} title="Konfidenz: Breite des Ergebnisbands">
            {mc.sampleCount?.toLocaleString('de-DE') ?? '-'} Samples · {conf.sigmaLabel} ({conf.label})
            {mc.seed != null && (
              <> · seed={mc.seed}</>
            )}
          </span>
        </div>
        <span
          title={`Cpk = ${num(cpk, 2)}`}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '3px 9px', borderRadius: 999,
            background: badge.bg, color: badge.color,
            fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, textTransform: 'uppercase',
          }}
        >
          {badge.label}
        </span>
      </div>

      {/* Capability + moments — 4-up grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 8 }}>
        <Tile
          label="μ"
          tooltip="Mittelwert (empirisch) der Monte-Carlo-Verteilung"
          value={num(mc.mean)}
          sub="empirisch"
        />
        <Tile
          label="σ"
          tooltip="Streuung (Standardabweichung) der Monte-Carlo-Verteilung"
          value={num(mc.stddev, 4)}
          sub={mc.k ? `±${mc.k}σ ≈ ${num(mc.k * mc.stddev)}` : null}
        />
        <Tile
          label="Cp"
          tooltip="Prozessfähigkeit (Cp) - Verhältnis von Toleranzbreite zu 6σ"
          value={num(cp, 2)}
        />
        <Tile
          label="Cpk"
          tooltip="Kritische Prozessfähigkeit (Cpk) - wie Cp, aber zentriert auf die Soll-Mitte"
          value={num(cpk, 2)}
          accent={badge.color}
        />
      </div>

      {/* Percentile strip — only when percentiles are present (engine
          always populates them but defensive: WC/RSS modes won't have
          mc at all, so this component returns null up top). */}
      {Object.keys(p).length > 0 && (
        <div>
          <div className="text-ink-3" style={{ fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase', marginBottom: 6 }}>
            Perzentile
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, minmax(0, 1fr))', gap: 6 }}>
            <Pct label="p1"     value={num(p.p1)} />
            <Pct label="p5"     value={num(p.p5)} />
            <Pct label="Median" value={num(p.p50)} />
            <Pct label="p95"    value={num(p.p95)} />
            <Pct label="p99"    value={num(p.p99)} />
          </div>
        </div>
      )}
    </div>
  );
}
